<template>
<section class="w1312 main-content-wrap">
  <div class="course-page">
    <!-- 顶部分类筛选栏 -->
    <div class="category-filter-bar">
      <span
        class="filter-item" :class="{'active': currentCategoryId === 0}"
        @click="selectCategory(0)"
      >全部</span>
      <span
        v-for="(item, index) in categoryList"
        :key="item.categoryId || index"
        class="filter-item"
        :class="{'active': isCategoryActive(item.categoryId)}"
        @click="selectCategory(item.categoryId)"
        @mouseenter="showSub(item)"
        @mouseleave="hideSub"
      >
        {{item.categoryName}}
        <div
          class="sub-filter-dropdown"
          v-if="hoveredCatId === item.categoryId && item.children && item.children.length > 0"
          style="display: block"
          @mouseenter.stop="keepSub"
        >
          <div
            v-for="child in item.children"
            :key="child.categoryId"
            class="sub-item-row"
            :class="{'has-nested': child.children && child.children.length}"
            @mouseenter="showSubDeep(child)"
            @mouseleave="hideSubDeep"
          >
            <span
              class="sub-item"
              :class="{'active': isCategoryActive(child.categoryId)}"
              @click.stop="selectCategory(child.categoryId)"
            >{{child.categoryName}}</span>
            <i class="arrow-right" v-if="child.children && child.children.length"></i>
            <div
              class="sub-filter-dropdown nested"
              v-if="hoveredDeepId === child.categoryId && child.children && child.children.length > 0"
              style="display: block"
              @mouseenter.stop="keepSubDeep"
            >
              <category-tree-node
                :items="child.children"
                :active-id="currentCategoryId"
                :active-ids="activeCategoryIds"
                @select="selectCategory"
              />
            </div>
          </div>
        </div>
      </span>
    </div>

    <!-- 排序栏 -->
    <div class="toolbar">
      <div class="sort-tabs">
        <span v-for="item in sortTabs" :key="item.value"
          :class="{'active': currentSort === item.value}"
          @click="switchSort(item.value)"
        >{{item.label}}</span>
      </div>
      <div class="sort-order">
        <span :class="{'active': currentOrder === 0}" @click="switchOrder(0)">倒序</span>
        <span :class="{'active': currentOrder === 1}" @click="switchOrder(1)">正序</span>
      </div>
      <div class="course-count" v-if="courseTotal > 0">共 {{courseTotal}} 个课程</div>
    </div>

    <!-- 课程列表 -->
    <div class="course-list">
      <view-container :loading="loading" :empty="empty" :error="error" @retry="fetchCourses">
        <ul>
          <li v-for="(item, index) in courseOverviewsList" :key="item.courseId || index">
            <dl>
              <dt>
                <router-link :to="{ name: 'CourseDetail', params: {target: sessionTarget, courseId: item.courseId}}">
                  <imgWrap :src="item.coverUrl" width="304" height="194" :alt="item.title" :item="item" :pic="'coverUrl'" />
                </router-link>
              </dt>
              <dd>
                <p class="list-title">
                  <router-link :to="{ name: 'CourseDetail', params: {target: sessionTarget, courseId: item.courseId}}">
                    {{item.title}}
                  </router-link>
                </p>
                <p class="list-tips">
                  <span v-if="item.duration">时长 {{parseDuration(item.duration)}}</span>
                  <!-- <span v-if="item.hot"><i class="eye-icon"></i>{{item.hot}}</span> -->
                  <span v-if="item.learningCount > 0" class="learning-count">{{item.learningCount}}人学习</span>
                  <span v-if="item.avgRating > 0" style="font-size:12px; color:#FD3374;" class="avg-rating">  
                    {{item.avgRating}}分
                  </span>
                </p>
              </dd>
            </dl>
          </li>
        </ul>
      </view-container>
    </div>

    <pagination v-model:pageModal="page" @query="fetchCourses()"/>
  </div>
</section>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { defineComponent, h } from 'vue'
import imgWrap from '@/components/imgWrap'
import pagination from '@/components/pagination.vue'
import util from '@/util/index.js'

const CategoryTreeNode = defineComponent({
  name: 'CategoryTreeNode',
  props: {
    items: { type: Array, default: () => [] },
    activeId: { type: Number, default: 0 },
    activeIds: { type: Set, default: () => new Set() }
  },
  emits: ['select'],
  data () {
    return { hoveredId: null, timer: null }
  },
  methods: {
    show (id) { clearTimeout(this.timer); this.hoveredId = id },
    keep () { clearTimeout(this.timer) },
    hide () { this.timer = setTimeout(() => { this.hoveredId = null }, 200) },
    isActive (id) { return this.activeId === id || this.activeIds.has(id) }
  },
  render () {
    return h('div', {}, this.items.map(item => {
      const hasKids = item.children && item.children.length > 0
      return h('div', {
        class: ['sub-item-row', { 'has-nested': hasKids }],
        key: item.categoryId,
        onMouseenter: () => this.show(item.categoryId),
        onMouseleave: () => this.hide()
      }, [
        h('span', {
          class: ['sub-item', { active: this.isActive(item.categoryId) }],
          onClick: (e) => { e.stopPropagation(); this.$emit('select', item.categoryId) }
        }, item.categoryName),
        hasKids ? h('i', { class: 'arrow-right' }) : null,
        hasKids && this.hoveredId === item.categoryId ? h('div', {
          class: 'sub-filter-dropdown nested',
          style: { display: 'block' },
          onMouseenter: (e) => { e.stopPropagation(); this.keep() }
        }, [
          h(CategoryTreeNode, {
            items: item.children,
            activeId: this.activeId,
            activeIds: this.activeIds,
            onSelect: (id) => this.$emit('select', id)
          })
        ]) : null
      ])
    }))
  }
})

export default {
  name: 'Course',
  components: { imgWrap, pagination, CategoryTreeNode },
  data () {
    return {
      categoryList: [],
      categoryAncestors: {},
      activeCategoryIds: new Set(),
      currentCategoryId: 0,
      currentSort: 0,
      currentOrder: 0,
      hoveredCatId: null,
      hoveredDeepId: null,
      hoverTimer: null,
      deepTimer: null,
      page: { pageNo: 1, pageSize: 12, total: 0 },
      courseTotal: 0,
      sortTabs: [
        { label: '发布时间', value: 0 },
        // { label: '按热度', value: 1 },
        { label: '课程评分', value: 2 },
        { label: '学习人数', value: 3 }
      ],
      loading: false, empty: false, error: false
    }
  },
  computed: {
    ...mapState(['courseOverviewsList', 'sessionTarget', 'queryUserPromise'])
  },
  mounted () {
    this.queryUserPromise.then(() => this.initFromQuery())
  },
  methods: {
    ...mapActions(['queryCategoryTree', 'queryCourseOverviews', 'queryCourseCount']),
    initFromQuery () {
      const q = this.$route.query
      this.currentCategoryId = Number(q.categoryId) || 0
      this.currentSort = Number(q.sortType) >= 0 ? Number(q.sortType) : 0
      this.currentOrder = Number(q.sortOrder) >= 0 ? Number(q.sortOrder) : 0
      this.loadCategories()
      this.fetchCourses()
    },
    loadCategories () {
      this.queryCategoryTree(true).then((resp) => {
        const raw = resp.children || []
        this.categoryList = raw
        this.buildAncestors(raw)
        this.activeCategoryIds = new Set(this.categoryAncestors[this.currentCategoryId] || [])
      })
    },
    buildAncestors (list, parents = []) {
      list.forEach(item => {
        this.categoryAncestors[item.categoryId] = new Set(parents)
        if (item.children && item.children.length) {
          this.buildAncestors(item.children, [...parents, item.categoryId])
        }
      })
    },
    isCategoryActive (id) {
      return this.currentCategoryId === id || this.activeCategoryIds.has(id)
    },
    selectCategory (categoryId) {
      this.currentCategoryId = categoryId
      this.activeCategoryIds = new Set(this.categoryAncestors[categoryId] || [])
      this.page.pageNo = 1
      this.updateRoute()
      this.fetchCourses()
    },
    updateRoute () {
      this.$router.replace({
        name: 'Course',
        params: { target: this.sessionTarget },
        query: {
          categoryId: this.currentCategoryId || undefined,
          sortType: this.currentSort || undefined,
          sortOrder: this.currentOrder || undefined
        }
      }).catch(() => {})
    },
    showSub (item) { clearTimeout(this.hoverTimer); this.hoveredCatId = item.categoryId },
    keepSub () { clearTimeout(this.hoverTimer) },
    hideSub () { this.hoverTimer = setTimeout(() => { this.hoveredCatId = null }, 200) },
    showSubDeep (item) { clearTimeout(this.deepTimer); this.hoveredDeepId = item.categoryId },
    keepSubDeep () { clearTimeout(this.deepTimer) },
    hideSubDeep () { this.deepTimer = setTimeout(() => { this.hoveredDeepId = null }, 200) },
    switchSort (sortType) {
      this.currentSort = sortType
      this.page.pageNo = 1
      this.updateRoute()
      this.fetchCourses()
    },
    switchOrder (order) {
      this.currentOrder = order
      this.page.pageNo = 1
      this.updateRoute()
      this.fetchCourses()
    },
    fetchCourses () {
      this.loading = true
      this.error = false
      this.empty = false
      const { pageNo, pageSize } = this.page
      Promise.all([
        this.queryCourseOverviews({
          categoryId: this.currentCategoryId,
          pageNo, pageSize,
          sortType: this.currentSort,
          sortOrder: this.currentOrder
        }),
        this.queryCourseCount(this.currentCategoryId)
      ]).then((resp) => {
        this.page.total = Number(resp[1]) || 0
        this.courseTotal = Number(resp[1]) || 0
        if (!this.courseTotal) this.empty = true
      }).catch(() => { this.error = true })
        .finally(() => { this.loading = false })
    },
    parseDuration: util.parseDuration
  }
}
</script>

<style lang="scss">
.course-page { margin-top: 25px; }

.category-filter-bar {
  background: #fff; border-radius: 8px; padding: 12px 24px;
  display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 16px;

  .filter-item {
    position: relative; display: inline-block; padding: 8px 16px;
    font-size: 14px; color: #666; cursor: pointer; border-radius: 4px; transition: all 0.2s;
    &:hover { color: #FD3374; background: #FFF5F7; }
    &.active { color: #fff; background: #FD3374; }

    .sub-filter-dropdown {
      display: none; position: absolute; left: 0; top: calc(100% - 2px);
      background: #fff; border: 1px solid #F0F0F0; border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.1); min-width: 140px; padding: 6px 0;
      z-index: 100;
      &.nested { left: 100%; top: 0px; margin-top: 0; margin-left: 2px; }.nested { left: 100%; top: 0px; margin-top: 0; }
    }
    &:hover > .sub-filter-dropdown { display: block; }
  }
}

.sub-item-row {
  display: flex; align-items: center; justify-content: space-between; position: relative;
  .sub-item {
    display: block; padding: 8px 12px 8px 16px; font-size: 13px; color: #666;
    white-space: nowrap; flex: 1; cursor: pointer; transition: all 0.15s;
    &:hover { color: #FD3374; background: #FFF5F7; }
    &.active { color: #FD3374; font-weight: 500; }
  }
  .arrow-right {
    width: 0; height: 0; border-style: solid; border-width: 4px 0 4px 5px;
    border-color: transparent transparent transparent #ccc; margin-right: 8px; flex-shrink: 0;
  }
  .sub-filter-dropdown { display: none; }
  &:hover > .sub-filter-dropdown { display: block; }
  &:hover > .arrow-right { border-left-color: #FD3374; }
}

.toolbar {
  display: flex; align-items: center; margin-bottom: 16px; gap: 16px;
  .sort-tabs {
    display: flex; gap: 0;
    span {
      font-size: 14px; border: 1px solid #ccc; padding: 6px 18px; cursor: pointer; color: #666;
      margin-left: -1px; background: #fff;
      &:first-child { border-radius: 5px 0 0 5px; margin-left: 0; }
      &:last-child { border-radius: 0 5px 5px 0; }
      &.active { border-color: #FD3374; color: #FD3374; z-index: 1; position: relative; }
    }
  }
  .sort-order {
    display: flex; gap: 0; margin-left: 8px;
    span {
      font-size: 13px; border: 1px solid #ddd; padding: 4px 12px; cursor: pointer; color: #999; background: #fff;
      &:first-child { border-radius: 4px 0 0 4px; }
      &:last-child { border-radius: 0 4px 4px 0; margin-left: -1px; }
      &.active { border-color: #FD3374; color: #FD3374; z-index: 1; position: relative; }
    }
  }
  .course-count { margin-left: auto; font-size: 14px; color: #999; }
}

.course-list {
  ul {
    display: flex; flex-wrap: wrap; margin-left: -16px; margin-right: -16px;
    li { flex: 0 0 25%; margin-bottom: 25px; }
  }
  dl {
    background: #fff; border-radius: 8px; overflow: hidden; margin: 0 16px; height: 100%;
    dt { width: 304px; height: 194px; }
    dd {
      font-size: 16px; line-height: 22px; color: #333; font-weight: bold; padding-left: 12px; padding-top: 8px;
      .list-title {
        width: 274px; height: 44px; overflow: hidden; text-overflow: ellipsis;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; padding-top: 6px; padding-bottom: 3px;
      }
      p.list-tips {
        font-size: 12px; font-weight: normal; color: #666; text-align: right;
        padding: 5px 16px 5px 0; display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap;
        .eye-icon { background: url(../../assets/img/search_icon_show@2x.png) no-repeat; background-size: 14px; display: inline-block; width: 14px; height: 12px; margin-left: 10px; }
        .learning-count { color: #999; }
        .avg-rating { color: #FD3374; }
      }
    }
  }
}
</style>
